import type { SessionDto } from '@pos/api-client';
import type { AddressSource } from '@pos/design-system';
import { createContext, useContext, type ReactNode } from 'react';
import { crudApi, type CrudApi } from './crud';
import type { FieldDef, RecordValues, ResourceDef, ResourceMap } from './resources';
import { resourcePath } from './resources';

/** What a custom field editor gets: the field, its value and error, the whole form, and how to change the value. */
export interface FieldEditorProps {
  field: FieldDef;
  value: unknown;
  /** A server error for this field. */
  invalidText?: string;
  values: Readonly<Record<string, unknown>>;
  onChange(value: unknown): void;
}

export interface ResourceExtension {
  /** Extra toolbar buttons on the resource's own list, before *Create*, e.g. "Create from a preset". */
  listToolbar?(list: { refresh(): void }): ReactNode;
  /** Quick filters above the resource's list, one click each next to *All*, e.g. "Only as an option". */
  quickFilters?: readonly { label: string; values: Readonly<Record<string, string>> }[];
  /** Extra buttons on the detail page, e.g. "Set password". */
  actions?(record: RecordValues): ReactNode;
  /** Extra tabs on the detail page, e.g. a visual designer. */
  tabs?: readonly { label: string; render(record: RecordValues): ReactNode }[];
  /** A child's tab shows this instead of the generated list, by child resource key, e.g. an editable price table. */
  childTabs?: Readonly<Record<string, (parent: RecordValues) => ReactNode>>;
  /** Extra toolbar buttons on a child's generated list, by child resource key, e.g. "Reset to default". */
  childToolbars?: Readonly<Record<string, (parent: RecordValues, list: { refresh(): void }) => ReactNode>>;
  /**
   * Create and edit on the record's own page (`/records/<resource>/new`, `/records/<resource>/<id>`) instead of a dialog.
   * Left out, a large form gets its own page by itself (`usesFormPage`); `false` keeps a large form in a dialog.
   */
  formPage?: boolean;
  /** Fields on a tab of their own on the record's page, named after the field, e.g. a rule builder; saved with the rest. */
  fieldTabs?: readonly string[];
  /** A field edited with its own control instead of the generated one, by field key, e.g. a rule builder for a JSON column. */
  fieldEditors?: Readonly<Record<string, (props: FieldEditorProps) => ReactNode>>;
}

/** An address the maps provider found: its point, and its street and the names of its parts as the provider writes them. */
export interface FoundAddress {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  street?: string | null;
  ward?: string | null;
  district?: string | null;
  province?: string | null;
  postalCode?: string | null;
}

/** An address the maps provider suggested for what was typed. */
export interface AddressSuggestion {
  placeId: string;
  mainText: string;
  secondaryText?: string | null;
}

/**
 * Where an address's point comes from, through the maps provider: suggestions as staff type and the one picked (`sessionToken`
 * ties them, so the provider bills them once), or the typed text geocoded, best match first.
 */
export interface AddressLookupSource {
  suggest(input: string, sessionToken: string): Promise<readonly AddressSuggestion[]>;
  resolve(placeId: string, sessionToken: string): Promise<FoundAddress>;
  find(address: string): Promise<readonly FoundAddress[]>;
}

export interface AdminContextValue {
  resources: ResourceMap;
  client: object;
  session: SessionDto;
  extensions: Readonly<Record<string, ResourceExtension>>;
  /** The location master data, for forms with an address (ADR 0026); without it they show the address columns as plain fields. */
  addressSource?: AddressSource;
  /**
   * Finding an address's latitude and longitude through a maps provider (HO's `Maps:Provider`): forms with an address offer it
   * above the address, and the address found fills the street, the point and the province and ward it matches. Without it,
   * nothing is offered.
   */
  addressLookup?: AddressLookupSource;
  /**
   * The languages translated fields (`*_i18n`) are edited in, e.g. the staff languages plus those of the brands' websites
   * (ADR 0071); without it, the staff languages.
   */
  textLanguages?: readonly string[];
  /**
   * Each record's History tab (ADR 0118): the audit log API of the portal and the permission to read it (`ho.audit.read` at HO,
   * `store.audit.read` at a store). Without it, or without the permission, record pages show no history.
   */
  history?: { path: string; permission: string };
}

export const AdminContext = createContext<AdminContextValue | null>(null);

export function useAdmin() {
  const value = useContext(AdminContext);
  if (!value) throw new Error('AdminProvider is missing');
  return value;
}

export function useResourceApi(def: ResourceDef, parentId?: string): CrudApi<RecordValues, Record<string, unknown>> {
  const { client } = useAdmin();
  return crudApi<RecordValues, Record<string, unknown>>(client, resourcePath(def, parentId));
}
